/**
 * WarrantyAssessmentPanel — Phase 16b1 reviewer panel.
 *
 * Collects the ASC 606-10-55-30 to 55-35 + ASC 460-10 warranty assessment
 * inputs on a per-POB basis: classification (NONE / ASSURANCE / SERVICE),
 * ASSURANCE probability gate (ASC 450-20-25-2 two-criterion), issuer type
 * (MANUFACTURER vs THIRD_PARTY_FOR_FEE), measurement method (EXPECTED_VALUE /
 * POINT_ESTIMATE_BEST / LOW_END_OF_RANGE), fair-value basis for the
 * third-party-for-fee path (ASC 460-10-30-2), and the ASC 606-10-55-31
 * separate-POB indicators (legally_required / separately_priced /
 * extends_beyond_defect). Includes RATABLE-POB attestation for the discrete
 * control-transfer assumption, a history viewer, a split-to-child bundled
 * workflow, and a manual control-transfer fire for RATABLE POBs.
 *
 * Props:
 *   pobId     — POB to configure
 *   pobLabel  — optional pretty label for the modal header
 *   recognitionMethod — 'RATABLE' | 'POINT_IN_TIME' | other (used to gate RATABLE sections)
 *   onClose   — close without saving
 *   onSuccess — called after successful PATCH
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import Modal from '../components/ui/Modal';
import DataTable, { Column } from '../components/ui/DataTable';

// ── Types ─────────────────────────────────────────────────────────

type WarrantyType = 'NONE' | 'ASSURANCE' | 'SERVICE';
type IssuerType = 'MANUFACTURER' | 'THIRD_PARTY_FOR_FEE';
type MeasurementMethod = 'EXPECTED_VALUE' | 'POINT_ESTIMATE_BEST' | 'LOW_END_OF_RANGE';
type ProbabilityAssessment = 'PROBABLE' | 'REASONABLY_POSSIBLE' | 'REMOTE';
type DiscreteTransferType =
  | 'EMBEDDED_HARDWARE'
  | 'STANDALONE_DELIVERABLE'
  | 'PROFESSIONAL_SERVICES_MILESTONE'
  | 'OTHER_REVIEWER_JUSTIFIED';

interface HistoryEvent {
  id: number;
  fired_at: string | null;
  event_type: string;
  amount: number | null;
  direction: string | null;
  je_id: number | null;
  triggered_by: string | null;
  rationale: string | null;
}

interface AccrualStatus {
  pob_id: number;
  warranty_type?: WarrantyType;
  warranty_issuer_type?: IssuerType | null;
  warranty_measurement_method?: MeasurementMethod | null;
  warranty_probability_assessment?: ProbabilityAssessment | null;
  warranty_amount_reasonably_estimable?: boolean | null;
  warranty_legally_required?: boolean | null;
  warranty_separately_priced?: boolean | null;
  warranty_extends_beyond_defect?: boolean | null;
  warranty_fair_value_basis_text?: string | null;
  warranty_expected_claim_rate?: number | null;
  warranty_expected_claim_cost?: number | null;
  warranty_expected_units?: number | null;
  warranty_point_estimate?: number | null;
  warranty_range_low?: number | null;
  warranty_range_high?: number | null;
  warranty_ratable_discrete_transfer_attested?: boolean | null;
  warranty_discrete_transfer_type?: DiscreteTransferType | null;
  warranty_discrete_transfer_evidence_reference?: string | null;
  warranty_discrete_transfer_description?: string | null;
  current_accrued_balance?: number | null;
  recognition_method?: string | null;
  history?: HistoryEvent[];
}

interface Props {
  pobId: number;
  pobLabel?: string;
  recognitionMethod?: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────

const DISCRETE_TYPE_OPTIONS: Array<{ value: DiscreteTransferType; label: string }> = [
  { value: 'EMBEDDED_HARDWARE', label: 'Embedded hardware (integral with delivered goods)' },
  { value: 'STANDALONE_DELIVERABLE', label: 'Standalone deliverable (stage-gated)' },
  { value: 'PROFESSIONAL_SERVICES_MILESTONE', label: 'Professional services milestone' },
  { value: 'OTHER_REVIEWER_JUSTIFIED', label: 'Other (reviewer-justified)' },
];

function parseNumOrNull(s: string): number | null {
  if (s === '' || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export default function WarrantyAssessmentPanel({
  pobId,
  pobLabel,
  recognitionMethod,
  onClose,
  onSuccess,
}: Props) {
  const { entity } = useGlobalEntity();
  const isRatable = (recognitionMethod || '').toUpperCase() === 'RATABLE';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<AccrualStatus | null>(null);

  // Form state
  const [warrantyType, setWarrantyType] = useState<WarrantyType>('NONE');
  const [probability, setProbability] = useState<ProbabilityAssessment | ''>('');
  const [amountEstimable, setAmountEstimable] = useState<boolean>(false);
  const [issuerType, setIssuerType] = useState<IssuerType | ''>('');
  const [measurementMethod, setMeasurementMethod] = useState<MeasurementMethod | ''>('');

  // Per-method inputs (strings for free typing; coerced on submit)
  const [claimRatePct, setClaimRatePct] = useState('');   // e.g. "2.5" for 2.5%
  const [claimCost, setClaimCost] = useState('');
  const [units, setUnits] = useState('');
  const [pointEstimate, setPointEstimate] = useState('');
  const [rangeLow, setRangeLow] = useState('');
  const [rangeHigh, setRangeHigh] = useState('');

  const [fvBasisText, setFvBasisText] = useState('');

  // 55-31 indicators
  const [legallyRequired, setLegallyRequired] = useState(false);
  const [separatelyPriced, setSeparatelyPriced] = useState(false);
  const [extendsBeyondDefect, setExtendsBeyondDefect] = useState(false);

  // RATABLE attestation
  const [ratableAttested, setRatableAttested] = useState(false);
  const [discreteType, setDiscreteType] = useState<DiscreteTransferType | ''>('');
  const [discreteEvidenceRef, setDiscreteEvidenceRef] = useState('');
  const [discreteDescription, setDiscreteDescription] = useState('');

  // Submission + tabs
  const [submitting, setSubmitting] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [bannerSuccess, setBannerSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'assess' | 'history'>('assess');

  // Sub-modals
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitChildPortion, setSplitChildPortion] = useState('');
  const [splitRationale, setSplitRationale] = useState('');
  const [splitSubmitting, setSplitSubmitting] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);

  const [fireOpen, setFireOpen] = useState(false);
  const [fireDate, setFireDate] = useState('');
  const [fireEvidence, setFireEvidence] = useState('');
  const [fireReaffirm, setFireReaffirm] = useState(false);
  const [fireSubmitting, setFireSubmitting] = useState(false);
  const [fireError, setFireError] = useState<string | null>(null);

  // ── Load ─────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await authFetch(
        withEntityParam(
          `${API_V1}/revenue/pobs/${pobId}/warranty/accrual-status`,
          entity,
        ),
      );
      if (!res.ok) {
        setLoadError(`Failed to load warranty status (HTTP ${res.status}).`);
        return;
      }
      const data: AccrualStatus = await res.json();
      setStatus(data);

      // Pre-populate form from server state
      setWarrantyType((data.warranty_type as WarrantyType) || 'NONE');
      setProbability((data.warranty_probability_assessment as ProbabilityAssessment) || '');
      setAmountEstimable(!!data.warranty_amount_reasonably_estimable);
      setIssuerType((data.warranty_issuer_type as IssuerType) || '');
      setMeasurementMethod((data.warranty_measurement_method as MeasurementMethod) || '');

      setClaimRatePct(
        data.warranty_expected_claim_rate != null
          ? (data.warranty_expected_claim_rate * 100).toString()
          : '',
      );
      setClaimCost(
        data.warranty_expected_claim_cost != null
          ? data.warranty_expected_claim_cost.toString()
          : '',
      );
      setUnits(
        data.warranty_expected_units != null
          ? data.warranty_expected_units.toString()
          : '',
      );
      setPointEstimate(
        data.warranty_point_estimate != null
          ? data.warranty_point_estimate.toString()
          : '',
      );
      setRangeLow(
        data.warranty_range_low != null
          ? data.warranty_range_low.toString()
          : '',
      );
      setRangeHigh(
        data.warranty_range_high != null
          ? data.warranty_range_high.toString()
          : '',
      );
      setFvBasisText(data.warranty_fair_value_basis_text || '');

      setLegallyRequired(!!data.warranty_legally_required);
      setSeparatelyPriced(!!data.warranty_separately_priced);
      setExtendsBeyondDefect(!!data.warranty_extends_beyond_defect);

      setRatableAttested(!!data.warranty_ratable_discrete_transfer_attested);
      setDiscreteType((data.warranty_discrete_transfer_type as DiscreteTransferType) || '');
      setDiscreteEvidenceRef(data.warranty_discrete_transfer_evidence_reference || '');
      setDiscreteDescription(data.warranty_discrete_transfer_description || '');
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load warranty status.');
    } finally {
      setLoading(false);
    }
  }, [pobId, entity]);

  useEffect(() => { load(); }, [load]);

  // ── Validation ───────────────────────────────────────────────────

  const isAssurance = warrantyType === 'ASSURANCE';
  const needsMeasurement = isAssurance && probability === 'PROBABLE' && amountEstimable;
  const needsFvBasis = isAssurance &&
    issuerType === 'THIRD_PARTY_FOR_FEE' &&
    measurementMethod === 'EXPECTED_VALUE';

  const rangeLowN = parseNumOrNull(rangeLow);
  const rangeHighN = parseNumOrNull(rangeHigh);
  const rangeValid = measurementMethod !== 'LOW_END_OF_RANGE'
    || (rangeLowN !== null && rangeHighN !== null && rangeLowN <= rangeHighN);

  const fvBasisLen = fvBasisText.trim().length;
  const discreteDescLen = discreteDescription.trim().length;

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (warrantyType === 'NONE') return true;
    if (warrantyType === 'SERVICE') {
      // SERVICE flows to Phase 10 allocation; still requires indicators.
      return true;
    }
    // ASSURANCE
    if (!probability) return false;
    // If PROBABLE + estimable => measurement required
    if (needsMeasurement) {
      if (!issuerType) return false;
      if (!measurementMethod) return false;
      if (measurementMethod === 'EXPECTED_VALUE') {
        if (parseNumOrNull(claimRatePct) === null) return false;
        if (parseNumOrNull(claimCost) === null) return false;
        if (parseNumOrNull(units) === null) return false;
      }
      if (measurementMethod === 'POINT_ESTIMATE_BEST') {
        if (parseNumOrNull(pointEstimate) === null) return false;
      }
      if (measurementMethod === 'LOW_END_OF_RANGE') {
        if (!rangeValid) return false;
      }
      if (needsFvBasis && fvBasisLen < 40) return false;
    }
    if (isRatable && isAssurance) {
      if (!ratableAttested) return false;
      if (!discreteType) return false;
      if (discreteEvidenceRef.trim().length === 0) return false;
      if (discreteDescLen < 40) return false;
    }
    return true;
  }, [
    loading, warrantyType, probability, needsMeasurement, issuerType, measurementMethod,
    claimRatePct, claimCost, units, pointEstimate, rangeValid,
    needsFvBasis, fvBasisLen, isRatable, isAssurance, ratableAttested,
    discreteType, discreteEvidenceRef, discreteDescLen,
  ]);

  // ── Submit: PATCH warranty-assessment ────────────────────────────

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setBannerError(null);
    setBannerSuccess(null);
    try {
      const body: any = {
        warranty_type: warrantyType,
      };
      if (warrantyType !== 'NONE') {
        body.warranty_legally_required = legallyRequired;
        body.warranty_separately_priced = separatelyPriced;
        body.warranty_extends_beyond_defect = extendsBeyondDefect;
      }
      if (warrantyType === 'ASSURANCE') {
        body.warranty_probability_assessment = probability;
        body.warranty_amount_reasonably_estimable = amountEstimable;
        if (needsMeasurement) {
          body.warranty_issuer_type = issuerType;
          body.warranty_measurement_method = measurementMethod;
          if (measurementMethod === 'EXPECTED_VALUE') {
            const rate = parseNumOrNull(claimRatePct);
            body.warranty_expected_claim_rate = rate !== null ? rate / 100 : null;
            body.warranty_expected_claim_cost = parseNumOrNull(claimCost);
            body.warranty_expected_units = parseNumOrNull(units);
          } else if (measurementMethod === 'POINT_ESTIMATE_BEST') {
            body.warranty_point_estimate = parseNumOrNull(pointEstimate);
          } else if (measurementMethod === 'LOW_END_OF_RANGE') {
            body.warranty_range_low = parseNumOrNull(rangeLow);
            body.warranty_range_high = parseNumOrNull(rangeHigh);
          }
          if (needsFvBasis) {
            body.warranty_fair_value_basis_text = fvBasisText.trim();
          }
        }
        if (isRatable) {
          body.warranty_ratable_discrete_transfer_attested = ratableAttested;
          body.warranty_discrete_transfer_type = discreteType;
          body.warranty_discrete_transfer_evidence_reference = discreteEvidenceRef.trim();
          body.warranty_discrete_transfer_description = discreteDescription.trim();
        }
      }

      const res = await authFetch(
        withEntityParam(
          `${API_V1}/revenue/pobs/${pobId}/warranty-assessment`,
          entity,
        ),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          const detail = data?.detail || data?.error || data?.message || data;
          msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } catch { /* keep status-only */ }
        setBannerError(msg);
        return;
      }
      setBannerSuccess('Warranty assessment saved.');
      if (onSuccess) onSuccess();
      // Refresh history + status
      load();
    } catch (e: any) {
      setBannerError(e?.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Bundled split ─────────────────────────────────────────────────

  const submitSplit = async () => {
    setSplitSubmitting(true);
    setSplitError(null);
    try {
      const body = {
        child_portion: parseNumOrNull(splitChildPortion),
        reviewer_attestation_text: splitRationale.trim(),
      };
      const res = await authFetch(
        withEntityParam(
          `${API_V1}/revenue/pobs/${pobId}/warranty/split-bundled`,
          entity,
        ),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          const detail = data?.detail || data?.error || data?.message || data;
          msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } catch { /* keep */ }
        setSplitError(msg);
        return;
      }
      setSplitOpen(false);
      setSplitChildPortion('');
      setSplitRationale('');
      setBannerSuccess('Bundled POB split recorded.');
      if (onSuccess) onSuccess();
      load();
    } catch (e: any) {
      setSplitError(e?.message || 'Split failed.');
    } finally {
      setSplitSubmitting(false);
    }
  };

  // ── Manual control-transfer fire ──────────────────────────────────

  const submitFire = async () => {
    setFireSubmitting(true);
    setFireError(null);
    try {
      const body = {
        control_transfer_date: fireDate,
        evidence_text: fireEvidence.trim(),
        reaffirm_at_fire: fireReaffirm,
      };
      const res = await authFetch(
        withEntityParam(
          `${API_V1}/revenue/pobs/${pobId}/warranty/record-control-transfer`,
          entity,
        ),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          const detail = data?.detail || data?.error || data?.message || data;
          msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } catch { /* keep */ }
        setFireError(msg);
        return;
      }
      setFireOpen(false);
      setFireDate('');
      setFireEvidence('');
      setFireReaffirm(false);
      setBannerSuccess('Control transfer recorded; warranty accrual fired.');
      if (onSuccess) onSuccess();
      load();
    } catch (e: any) {
      setFireError(e?.message || 'Fire failed.');
    } finally {
      setFireSubmitting(false);
    }
  };

  // ── Computed preview (client-side estimate only) ──────────────────

  const computedPreview: number | null = useMemo(() => {
    if (!isAssurance || !needsMeasurement) return null;
    if (measurementMethod === 'EXPECTED_VALUE') {
      const rate = parseNumOrNull(claimRatePct);
      const cost = parseNumOrNull(claimCost);
      const u = parseNumOrNull(units);
      if (rate === null || cost === null || u === null) return null;
      return (rate / 100) * cost * u;
    }
    if (measurementMethod === 'POINT_ESTIMATE_BEST') {
      return parseNumOrNull(pointEstimate);
    }
    if (measurementMethod === 'LOW_END_OF_RANGE') {
      return rangeLowN;
    }
    return null;
  }, [
    isAssurance, needsMeasurement, measurementMethod,
    claimRatePct, claimCost, units, pointEstimate, rangeLowN,
  ]);

  // ── History columns ────────────────────────────────────────────────

  const historyColumns: Column[] = [
    {
      key: 'fired_at',
      label: 'Fired at',
      render: (_v: any, row: HistoryEvent) =>
        row.fired_at ? new Date(row.fired_at).toLocaleString() : '-',
    },
    { key: 'event_type', label: 'Event' },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      render: (v: any) => (v != null ? formatAmount(Number(v)) : '-'),
    },
    { key: 'direction', label: 'Direction' },
    {
      key: 'je_id',
      label: 'JE',
      render: (v: any) => (v != null ? `#${v}` : '-'),
    },
    { key: 'triggered_by', label: 'Triggered by' },
    { key: 'rationale', label: 'Rationale' },
  ];

  const title = pobLabel
    ? `${pobLabel} - Warranty Assessment`
    : `POB #${pobId} - Warranty Assessment`;

  return (
    <>
      <Modal
        open={true}
        onClose={onClose}
        title={title}
        description="ASC 606-10-55-30 to 55-35 + ASC 460-10 + ASC 450-20. Configure classification, measurement, and control-transfer assumptions."
        size="lg"
        footer={
          <>
            <button onClick={onClose} disabled={submitting} style={btnStyle('secondary')}>
              Close
            </button>
            {activeTab === 'assess' && (
              <button
                onClick={submit}
                disabled={!canSubmit || submitting}
                style={{
                  ...btnStyle(canSubmit ? 'primary' : 'disabled'),
                  cursor: submitting ? 'wait' : (canSubmit ? 'pointer' : 'not-allowed'),
                }}
              >
                {submitting ? 'Saving...' : 'Save Assessment'}
              </button>
            )}
          </>
        }
      >
        {/* Tab strip */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 16 }}>
          {[
            { key: 'assess' as const, label: 'Assessment' },
            { key: 'history' as const, label: `History${status?.history ? ` (${status.history.length})` : ''}` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: activeTab === t.key ? 700 : 500,
                color: activeTab === t.key ? 'var(--navy-900)' : 'var(--text-secondary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === t.key ? '2px solid var(--navy-900)' : '2px solid transparent',
                marginBottom: -2,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>Loading...</div>}
        {loadError && <div style={errorBoxStyle}>{loadError}</div>}
        {bannerError && <div style={errorBoxStyle}>{bannerError}</div>}
        {bannerSuccess && <div style={successBoxStyle}>{bannerSuccess}</div>}

        {!loading && !loadError && activeTab === 'assess' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Classification */}
            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>Classification (ASC 606-10-55-30 to 55-35)</legend>
              <div style={{ display: 'flex', gap: 12 }}>
                {(['NONE', 'ASSURANCE', 'SERVICE'] as WarrantyType[]).map((t) => (
                  <label key={t} style={radioLabelStyle}>
                    <input
                      type="radio"
                      name="warranty-type"
                      checked={warrantyType === t}
                      onChange={() => setWarrantyType(t)}
                    />
                    <span>
                      <strong>{t}</strong>
                      {t === 'ASSURANCE' && (
                        <span style={{ color: 'var(--text-hint)', marginLeft: 6, fontSize: 11 }}>
                          — fix-to-specification; ASC 460 cost accrual
                        </span>
                      )}
                      {t === 'SERVICE' && (
                        <span style={{ color: 'var(--text-hint)', marginLeft: 6, fontSize: 11 }}>
                          — separate POB (55-34)
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {warrantyType !== 'NONE' && (
              <fieldset style={fieldsetStyle}>
                <legend style={legendStyle}>ASC 606-10-55-31 indicators</legend>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
                  <label style={checkLabelStyle}>
                    <input
                      type="checkbox"
                      checked={legallyRequired}
                      onChange={(e) => setLegallyRequired(e.target.checked)}
                    />
                    <span>
                      <strong>Legally required</strong>
                      <span style={hintStyle}>
                        warranty is mandated by law — evidence of assurance-type per 55-31(a).
                      </span>
                    </span>
                  </label>
                  <label style={checkLabelStyle}>
                    <input
                      type="checkbox"
                      checked={separatelyPriced}
                      onChange={(e) => setSeparatelyPriced(e.target.checked)}
                    />
                    <span>
                      <strong>Separately priced</strong>
                      <span style={hintStyle}>
                        customer can purchase separately — indicates SERVICE per 55-31(b).
                      </span>
                    </span>
                  </label>
                  <label style={checkLabelStyle}>
                    <input
                      type="checkbox"
                      checked={extendsBeyondDefect}
                      onChange={(e) => setExtendsBeyondDefect(e.target.checked)}
                    />
                    <span>
                      <strong>Extends beyond defect coverage</strong>
                      <span style={hintStyle}>
                        covers events beyond fix-to-specification — indicates SERVICE per 55-31(c).
                      </span>
                    </span>
                  </label>
                </div>
              </fieldset>
            )}

            {isAssurance && (
              <>
                {/* Probability gate */}
                <fieldset style={fieldsetStyle}>
                  <legend style={legendStyle}>
                    Probability gate (ASC 450-20-25-2 — two-criterion)
                  </legend>
                  <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 8 }}>
                    Accrual requires both (a) loss probable and (b) amount reasonably estimable.
                    PROBABLE but not estimable falls into the ASC 450-20-50-5 disclosure-only path.
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                    {(['PROBABLE', 'REASONABLY_POSSIBLE', 'REMOTE'] as ProbabilityAssessment[]).map((p) => (
                      <label key={p} style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="prob"
                          checked={probability === p}
                          onChange={() => setProbability(p)}
                        />
                        <span><strong>{p.replace(/_/g, ' ')}</strong></span>
                      </label>
                    ))}
                  </div>
                  <label style={checkLabelStyle}>
                    <input
                      type="checkbox"
                      checked={amountEstimable}
                      onChange={(e) => setAmountEstimable(e.target.checked)}
                    />
                    <span>
                      <strong>Amount reasonably estimable</strong>
                      <span style={hintStyle}>
                        if unchecked with PROBABLE, allowance is NOT accrued; disclosure-only applies.
                      </span>
                    </span>
                  </label>
                </fieldset>

                {/* Issuer type */}
                <fieldset style={fieldsetStyle}>
                  <legend style={legendStyle}>Issuer type</legend>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="issuer"
                        checked={issuerType === 'MANUFACTURER'}
                        onChange={() => setIssuerType('MANUFACTURER')}
                      />
                      <span>
                        <strong>MANUFACTURER</strong>
                        <span style={hintStyle}>— producer standing behind defects</span>
                      </span>
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="issuer"
                        checked={issuerType === 'THIRD_PARTY_FOR_FEE'}
                        onChange={() => setIssuerType('THIRD_PARTY_FOR_FEE')}
                      />
                      <span>
                        <strong>THIRD_PARTY_FOR_FEE</strong>
                        <span style={hintStyle}>— ASC 460 guarantee; fair-value measurement required</span>
                      </span>
                    </label>
                  </div>
                </fieldset>

                {/* Measurement method (only when PROBABLE + estimable) */}
                {needsMeasurement && (
                  <fieldset style={fieldsetStyle}>
                    <legend style={legendStyle}>Measurement method</legend>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="method"
                          checked={measurementMethod === 'EXPECTED_VALUE'}
                          onChange={() => setMeasurementMethod('EXPECTED_VALUE')}
                        />
                        <span><strong>EXPECTED_VALUE</strong> — claim_rate * claim_cost * units</span>
                      </label>
                      <label style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="method"
                          checked={measurementMethod === 'POINT_ESTIMATE_BEST'}
                          onChange={() => setMeasurementMethod('POINT_ESTIMATE_BEST')}
                        />
                        <span><strong>POINT_ESTIMATE_BEST</strong> — single most-likely amount</span>
                      </label>
                      <label style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="method"
                          checked={measurementMethod === 'LOW_END_OF_RANGE'}
                          onChange={() => setMeasurementMethod('LOW_END_OF_RANGE')}
                        />
                        <span><strong>LOW_END_OF_RANGE</strong> — ASC 450-20-30-1 when no best estimate</span>
                      </label>
                    </div>

                    {measurementMethod === 'EXPECTED_VALUE' && (
                      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                        <label style={inputLabelStyle}>
                          <span>Claim rate (%)</span>
                          <input
                            type="number"
                            step="0.01"
                            value={claimRatePct}
                            onChange={(e) => setClaimRatePct(e.target.value)}
                            placeholder="2.5"
                            style={inputStyle}
                          />
                        </label>
                        <label style={inputLabelStyle}>
                          <span>Claim cost (per unit)</span>
                          <input
                            type="number"
                            step="0.01"
                            value={claimCost}
                            onChange={(e) => setClaimCost(e.target.value)}
                            placeholder="125.00"
                            style={inputStyle}
                          />
                        </label>
                        <label style={inputLabelStyle}>
                          <span>Units</span>
                          <input
                            type="number"
                            value={units}
                            onChange={(e) => setUnits(e.target.value)}
                            placeholder="1000"
                            style={inputStyle}
                          />
                        </label>
                      </div>
                    )}

                    {measurementMethod === 'POINT_ESTIMATE_BEST' && (
                      <div style={{ marginTop: 10 }}>
                        <label style={inputLabelStyle}>
                          <span>Point estimate</span>
                          <input
                            type="number"
                            step="0.01"
                            value={pointEstimate}
                            onChange={(e) => setPointEstimate(e.target.value)}
                            placeholder="15000"
                            style={inputStyle}
                          />
                        </label>
                      </div>
                    )}

                    {measurementMethod === 'LOW_END_OF_RANGE' && (
                      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                        <label style={inputLabelStyle}>
                          <span>Range low</span>
                          <input
                            type="number"
                            step="0.01"
                            value={rangeLow}
                            onChange={(e) => setRangeLow(e.target.value)}
                            placeholder="10000"
                            style={inputStyle}
                          />
                        </label>
                        <label style={inputLabelStyle}>
                          <span>Range high</span>
                          <input
                            type="number"
                            step="0.01"
                            value={rangeHigh}
                            onChange={(e) => setRangeHigh(e.target.value)}
                            placeholder="25000"
                            style={inputStyle}
                          />
                        </label>
                        {!rangeValid && (
                          <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--negative)' }}>
                            Range low must be less than or equal to range high.
                          </div>
                        )}
                      </div>
                    )}

                    {needsFvBasis && (
                      <div style={{ marginTop: 10 }}>
                        <label style={inputLabelStyle}>
                          <span>Fair-value basis (ASC 460-10-30-2, min 40 chars)</span>
                          <textarea
                            value={fvBasisText}
                            onChange={(e) => setFvBasisText(e.target.value)}
                            placeholder="Describe the statistical basis: historical claim frequency, actuarial study, market quotes for similar guarantees..."
                            style={{ ...textareaStyle, minHeight: 60 }}
                            rows={4}
                          />
                        </label>
                        <div style={{
                          fontSize: 11,
                          color: fvBasisLen < 40 ? 'var(--warning)' : 'var(--text-hint)',
                          marginTop: 2,
                        }}>
                          {fvBasisLen} / 40 chars{fvBasisLen < 40 ? ' — more detail required' : ' — OK'}
                        </div>
                      </div>
                    )}

                    {computedPreview !== null && (
                      <div style={{
                        marginTop: 10, padding: 10,
                        background: 'rgba(59,130,246,0.08)',
                        border: '1px solid var(--accent)',
                        borderRadius: 4,
                        fontSize: 12,
                      }}>
                        <strong>Computed allowance preview:</strong>{' '}
                        {formatAmount(computedPreview)}
                        <span style={hintStyle}>
                          {' '}(client-side estimate — authoritative value computed server-side on save)
                        </span>
                      </div>
                    )}
                  </fieldset>
                )}

                {/* RATABLE attestation */}
                {isRatable && (
                  <fieldset style={{ ...fieldsetStyle, background: 'rgba(234,179,8,0.05)', borderColor: 'var(--warning)' }}>
                    <legend style={{ ...legendStyle, color: 'var(--warning)' }}>
                      RATABLE POB — discrete control-transfer attestation
                    </legend>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                      ASC 460-10-25-1 — ASSURANCE warranty accrues at a discrete control-transfer
                      event. For RATABLE POBs the accrual is decoupled from the revenue
                      recognition pattern and requires reviewer attestation.
                    </div>
                    <label style={checkLabelStyle}>
                      <input
                        type="checkbox"
                        checked={ratableAttested}
                        onChange={(e) => setRatableAttested(e.target.checked)}
                      />
                      <span>
                        <strong>I attest to a discrete control-transfer event for this RATABLE POB.</strong>
                      </span>
                    </label>

                    <div style={{ marginTop: 10 }}>
                      <label style={inputLabelStyle}>
                        <span>Discrete transfer type</span>
                        <select
                          value={discreteType}
                          onChange={(e) => setDiscreteType(e.target.value as DiscreteTransferType)}
                          style={inputStyle}
                        >
                          <option value="">-- select --</option>
                          {DISCRETE_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <label style={inputLabelStyle}>
                        <span>Evidence reference (e.g. "MSA Section 4.3", "GR-2026-00142")</span>
                        <input
                          type="text"
                          value={discreteEvidenceRef}
                          onChange={(e) => setDiscreteEvidenceRef(e.target.value)}
                          style={inputStyle}
                          placeholder="MSA Section 4.3"
                        />
                      </label>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <label style={inputLabelStyle}>
                        <span>Description (min 40 chars)</span>
                        <textarea
                          value={discreteDescription}
                          onChange={(e) => setDiscreteDescription(e.target.value)}
                          style={{ ...textareaStyle, minHeight: 60 }}
                          rows={4}
                          placeholder="Describe the discrete control-transfer event and why it decouples from the revenue pattern..."
                        />
                      </label>
                      <div style={{
                        fontSize: 11,
                        color: discreteDescLen < 40 ? 'var(--warning)' : 'var(--text-hint)',
                        marginTop: 2,
                      }}>
                        {discreteDescLen} / 40 chars
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* Action buttons (split / fire) */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {extendsBeyondDefect && (
                    <button
                      onClick={() => setSplitOpen(true)}
                      style={btnStyle('secondary')}
                    >
                      Split into child SERVICE POB
                    </button>
                  )}
                  {isRatable && (
                    <button
                      onClick={() => setFireOpen(true)}
                      style={btnStyle('secondary')}
                    >
                      Record control transfer
                    </button>
                  )}
                </div>
              </>
            )}

            {status?.current_accrued_balance != null && (
              <div style={{
                padding: 10,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                fontSize: 12,
              }}>
                <strong>Current accrued balance:</strong>{' '}
                {formatAmount(status.current_accrued_balance)}
              </div>
            )}
          </div>
        )}

        {!loading && !loadError && activeTab === 'history' && (
          <div>
            <DataTable
              columns={historyColumns}
              rows={status?.history || []}
              emptyMessage="No warranty accrual history yet."
            />
          </div>
        )}
      </Modal>

      {/* Split sub-modal */}
      {splitOpen && (
        <Modal
          open={splitOpen}
          onClose={() => setSplitOpen(false)}
          title="Split bundled warranty into child SERVICE POB"
          description="ASC 606-10-55-34 — SERVICE-type coverage beyond assurance is a separate POB."
          size="md"
          footer={
            <>
              <button
                onClick={() => setSplitOpen(false)}
                disabled={splitSubmitting}
                style={btnStyle('secondary')}
              >Cancel</button>
              <button
                onClick={submitSplit}
                disabled={
                  splitSubmitting ||
                  parseNumOrNull(splitChildPortion) === null ||
                  splitRationale.trim().length < 30
                }
                style={btnStyle(
                  parseNumOrNull(splitChildPortion) !== null && splitRationale.trim().length >= 30
                    ? 'primary' : 'disabled',
                )}
              >{splitSubmitting ? 'Submitting...' : 'Split'}</button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {splitError && <div style={errorBoxStyle}>{splitError}</div>}
            <label style={inputLabelStyle}>
              <span>Child portion (0-1 fraction of parent allocation)</span>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={splitChildPortion}
                onChange={(e) => setSplitChildPortion(e.target.value)}
                style={inputStyle}
                placeholder="0.15"
              />
            </label>
            <label style={inputLabelStyle}>
              <span>Reviewer attestation (min 30 chars)</span>
              <textarea
                value={splitRationale}
                onChange={(e) => setSplitRationale(e.target.value)}
                style={{ ...textareaStyle, minHeight: 60 }}
                rows={4}
                placeholder="Describe why coverage beyond defect makes this a SERVICE POB and how the child portion was determined..."
              />
            </label>
            <div style={{
              fontSize: 11,
              color: splitRationale.trim().length < 30 ? 'var(--warning)' : 'var(--text-hint)',
            }}>
              {splitRationale.trim().length} / 30 chars
            </div>
          </div>
        </Modal>
      )}

      {/* Fire sub-modal */}
      {fireOpen && (
        <Modal
          open={fireOpen}
          onClose={() => setFireOpen(false)}
          title="Record control transfer (manual RATABLE fire)"
          description="Records the discrete control-transfer event and fires the ASSURANCE accrual."
          size="md"
          footer={
            <>
              <button
                onClick={() => setFireOpen(false)}
                disabled={fireSubmitting}
                style={btnStyle('secondary')}
              >Cancel</button>
              <button
                onClick={submitFire}
                disabled={fireSubmitting || !fireDate || fireEvidence.trim().length === 0}
                style={btnStyle(
                  fireDate && fireEvidence.trim().length > 0 ? 'primary' : 'disabled',
                )}
              >{fireSubmitting ? 'Firing...' : 'Record transfer'}</button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {fireError && <div style={errorBoxStyle}>{fireError}</div>}
            <label style={inputLabelStyle}>
              <span>Control transfer date</span>
              <input
                type="date"
                value={fireDate}
                onChange={(e) => setFireDate(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={inputLabelStyle}>
              <span>Evidence text</span>
              <textarea
                value={fireEvidence}
                onChange={(e) => setFireEvidence(e.target.value)}
                style={{ ...textareaStyle, minHeight: 60 }}
                rows={4}
                placeholder="Signed delivery receipt #12345; Customer acceptance email dated 2026-04-18..."
              />
            </label>
            <label style={checkLabelStyle}>
              <input
                type="checkbox"
                checked={fireReaffirm}
                onChange={(e) => setFireReaffirm(e.target.checked)}
              />
              <span>
                <strong>Reaffirm assessment inputs at fire</strong>
                <span style={hintStyle}>
                  re-runs the measurement with current inputs rather than the initially-saved snapshot.
                </span>
              </span>
            </label>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Styles ───────────────────────────────────────────────────────────

const fieldsetStyle: React.CSSProperties = {
  padding: 12,
  border: '1px solid var(--border-subtle)',
  borderRadius: 6,
  background: 'var(--bg-card)',
  margin: 0,
};

const legendStyle: React.CSSProperties = {
  padding: '0 6px',
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--navy-900)',
};

const radioLabelStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
  cursor: 'pointer',
  fontSize: 13,
};

const checkLabelStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'flex-start',
  cursor: 'pointer',
  fontSize: 12,
};

const hintStyle: React.CSSProperties = {
  display: 'block',
  color: 'var(--text-hint)',
  fontSize: 11,
  marginTop: 2,
};

const inputLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  padding: 6,
  fontSize: 12,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 4,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  width: '100%',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: 6,
  fontSize: 12,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 4,
  color: 'var(--text-primary)',
  resize: 'vertical',
  minHeight: 36,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 10,
  background: 'rgba(239,68,68,0.1)',
  border: '1px solid var(--negative)',
  borderRadius: 4,
  color: 'var(--negative)',
  fontSize: 12,
};

const successBoxStyle: React.CSSProperties = {
  padding: 10,
  background: 'rgba(34,197,94,0.1)',
  border: '1px solid var(--positive)',
  borderRadius: 4,
  color: 'var(--positive)',
  fontSize: 12,
};

function btnStyle(variant: 'primary' | 'secondary' | 'disabled'): React.CSSProperties {
  if (variant === 'primary') {
    return {
      padding: '8px 16px', fontSize: 13, fontWeight: 600,
      background: 'var(--positive)', color: 'white',
      border: 'none', borderRadius: 4, cursor: 'pointer',
    };
  }
  if (variant === 'disabled') {
    return {
      padding: '8px 16px', fontSize: 13, fontWeight: 600,
      background: 'var(--bg-hover)', color: 'var(--text-hint)',
      border: 'none', borderRadius: 4, cursor: 'not-allowed',
    };
  }
  return {
    padding: '8px 16px', fontSize: 13,
    background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer',
  };
}
